import React, { useCallback, useEffect, useState } from 'react';
import {
  editPF25Admin,
  createPF25Admin,
  activatePFAdmin,
  deactivatePFAdmin,
  createSLHManager,
  editSLHManager,
  deactivateSLHManager,
  activateSLHManager,
  resendActivationInvitation,
} from 'api/api';
import {
  SelectOption,
  OrangeButton,
  IconSmallArrow,
  FormInput,
  IconSoberLiving,
  IconInfo,
  SmallButton,
} from 'components';
import { COLORS } from 'utils/colors';
import { Form, Tooltip } from 'antd';
import GlobalModal from 'components/globalModal';
import { useAuth } from 'context/authContext';
import { allowOnlyDigits } from 'utils/helpers';
import { ROLE_TYPES } from 'api/constants';
import { getRoleFromHostname } from 'api/roleConfig';

export const AddEditPFAdminModal = ({
  adminData,
  onSuccess,
  onClose,
  isOpen,
  isEditMode,
  partnerID,
  role,
  slhPartnerId,
  isInAdminViewPage,
}) => {
  const [formData, setFormData] = useState({
    firstname: isEditMode ? adminData?.firstname : '',
    lastname: isEditMode ? adminData?.lastname : '',
    email: isEditMode ? adminData?.email : '',
    country_code: isEditMode ? adminData?.country_code : '+1',
    phone: isEditMode ? adminData?.phone : '',
    gender: '',
  });
  const { partnerId } = useAuth();
  const roleType = getRoleFromHostname(window.location.hostname);
  const onValuesChange = (_, allValues) => {
    const { firstname, lastname, email, phone } = allValues;

    setFormData({ ...formData, firstname, lastname, email, phone });
  };

  useEffect(() => {
    if (isEditMode && adminData && isInAdminViewPage) {
      setFormData({
        firstname: adminData?.firstname || '',
        lastname: adminData?.lastname || '',
        email: adminData?.email || '',
        country_code: adminData?.country_code || '+1',
        phone: adminData?.phone || '',
        gender: adminData?.gender || '',
      });
    }
  }, [adminData, isEditMode, isInAdminViewPage]);

  const handleFormSubmit = useCallback(() => {
    if (isEditMode) {
      if (partnerID || (isInAdminViewPage && role === ROLE_TYPES.slh_manager)) {
        editSLHManager(
          {
            ...formData,
            partnerId: partnerID || slhPartnerId,
            phone: formData.phone,
            type: 'manager',
          },
          roleType,
          isInAdminViewPage ? adminData?.user_id : adminData?.id,
          () => {
            onClose();
            onSuccess();
          },
        );
      } else {
        editPF25Admin(
          {
            ...formData,
            partnerId: partnerId,
            phone: formData.phone,
            type: 'admin',
          },
          role,
          isInAdminViewPage ? adminData?.user_id : adminData?.id,
          () => {
            onClose();
            onSuccess();
          },
        );
      }
    } else {
      if (partnerID) {
        createSLHManager(
          {
            ...formData,
            partnerId: partnerID,
            phone: formData.phone,
            type: 'manager',
          },
          role,
          () => {
            onClose();
            onSuccess();
          },
        );
      } else {
        createPF25Admin(
          {
            ...formData,
            partnerId: partnerId,
            phone: formData.phone,
            type: 'admin',
          },
          role,
          () => {
            onClose();
            onSuccess();
          },
        );
      }
    }
  }, [
    isEditMode,
    partnerID,
    isInAdminViewPage,
    role,
    formData,
    slhPartnerId,
    roleType,
    adminData?.user_id,
    adminData?.id,
    onClose,
    onSuccess,
    partnerId,
  ]);

  const handleAdminTypeChange = (value) => {
    setFormData({
      ...formData,
      type: value,
    });
  };

  const handlePF25AdminStatusChange = useCallback(() => {
    if (adminData?.status === 'active') {
      return (
        <SmallButton
          btnClassName="red"
          label="Deactivate"
          onClick={() => {
            if (partnerID || (isInAdminViewPage && role === ROLE_TYPES.slh_manager)) {
              deactivateSLHManager(
                role,
                isInAdminViewPage ? adminData?.user_id : adminData?.id,
                () => {
                  onClose();
                  window.location.reload();
                },
              );
            } else {
              deactivatePFAdmin(
                role,
                isInAdminViewPage ? adminData?.user_id : adminData?.id,
                () => {
                  onClose();
                  window.location.reload();
                },
              );
            }
          }}
        />
      );
    }

    if (adminData?.status === 'pending') {
      return (
        <SmallButton
          btnClassName="orange"
          label="Resend"
          onClick={() => {
            resendActivationInvitation(isInAdminViewPage ? ROLE_TYPES.admin :role, 
              adminData?.id ?? adminData?.user_id, () => {
              onClose();
            });
          }}
        />
      );
    }
    return (
      <SmallButton
        btnClassName="green"
        label="Activate"
        onClick={() => {
          if (partnerID || (isInAdminViewPage && role === ROLE_TYPES.slh_manager)) {
            activateSLHManager(role, isInAdminViewPage ? adminData?.user_id : adminData?.id, () => {
              onClose();
              window.location.reload();
            });
          } else {
            activatePFAdmin(role, isInAdminViewPage ? adminData?.user_id : adminData?.id, () => {
              onClose();
              window.location.reload();
            });
          }
        }}
      />
    );
  }, [
    adminData?.id,
    adminData?.status,
    adminData?.user_id,
    isInAdminViewPage,
    onClose,
    partnerID,
    role,
  ]);

  const sanitizeDigitsOnInput = (e) => {
    const input = e.target;
    const rawValue = input.value;
    const digitsOnly = rawValue.replace(/\D/g, '');

    const prefixDigits = formData.country_code.replace(/\D/g, '');
    const sanitized = digitsOnly.startsWith(prefixDigits)
      ? digitsOnly.slice(prefixDigits.length)
      : digitsOnly;

    if (rawValue !== sanitized) {
      input.value = sanitized;
    }
  };

  return (
    <GlobalModal
      width={510}
      open={isOpen}
      title={`${isEditMode ? 'Edit' : 'Add'} ${partnerID ? 'SLH Manager' : 'PF25 Admin'}`}
      onClose={onClose}
      titleLeft
    >
      <Form
        requiredMark={'optional'}
        name="terminate_form"
        className="flex flex-col p-[30px] gap-y-6"
        initialValues={{ ...formData }}
        onValuesChange={onValuesChange}
        onFinish={handleFormSubmit}
      >
        <FormInput
          label="Name*"
          name="firstname"
          placeholder="First Name"
          defaultValue={formData.firstname}
          required
        />
        <FormInput
          label="Last Name*"
          name="lastname"
          defaultValue={formData.lastname}
          placeholder="Last Name"
          required
        />
        <FormInput
          className="w-full"
          type={'email'}
          defaultValue={formData.email}
          disabled={isEditMode}
          label={
            <div className="flex ">
              <p>Email*</p>
              <Tooltip
                className="ml-1"
                title="This email will be used as SLH Managed Login credential"
                overlayInnerStyle={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  lineHeight: '16px',
                  fontFamily: 'poppins',
                  backgroundColor: '#000',
                }}
                placement="top"
              >
                <IconInfo color={COLORS.blue} />
              </Tooltip>
            </div>
          }
          name="email"
          placeholder="Email"
          required
        />
        <FormInput
          label="Phone"
          defaultValue={formData.phone}
          name="phone"
          type="text"
          pattern="['0-9']*"
          maxLength={10}
          placeholder="Phone"
          prefix={formData.country_code || '+1'}
          onKeyDown={allowOnlyDigits}
          onWheel={(e) => {
            e.target.blur();
            e.stopPropagation();

            setTimeout(() => {
              e.target.focus();
            }, 0);
          }}
          onInput={sanitizeDigitsOnInput}
        />
        <div className="flex flex-col w-full gap-y-1">
          <p className="text-14 font-medium">Type</p>
          <SelectOption
            onSelect={(value) => handleAdminTypeChange(value)}
            dropdownStyle={{ padding: '16px', background: 'white' }}
            required
            suffixIcon={<IconSmallArrow color={COLORS.black} className="rotate-[-90deg] w-3 h-3" />}
            className="h-[44px] text-base font-medium base-block rounded-lg"
            placeholder="Type"
            defaultValue={
              partnerID || (isInAdminViewPage && role === ROLE_TYPES.slh_manager) ? '2' : '1'
            }
            dropdownRender={(originalNode) => originalNode}
            optionRender={(option) => (
              <div className="flex items-center px-2 py-1 gap-x-1 text-black rounded-md capitalize duration-150 hover:bg-orange">
                <IconSoberLiving />
                <p className="font-14">{option.label}</p>
              </div>
            )}
            disabled
            options={[
              { label: 'Admin', value: '1' },
              { label: 'Manager', value: '2' },
            ]}
          />
        </div>
        <div className="flex justify-between items-center w-full">
          {isEditMode && handlePF25AdminStatusChange()}
          <OrangeButton
            type="submit"
            label={`${isEditMode ? 'Save' : 'Add'}`}
            className="self-end"
          />
        </div>
      </Form>
    </GlobalModal>
  );
};
